/**
 * GenUIFrame — sandboxed iframe for agent-generated interactive HTML.
 *
 * Renders HTML content in a sandboxed iframe with a `cowork` bridge injected
 * for communication back to the host app via postMessage:
 *
 *   cowork.sendMessage(text)   — visible user chat message
 *   cowork.sendAction(payload) — silent structured data to the agent
 *   cowork.close()             — minimize the widget to a compact bar
 *
 * The iframe auto-resizes to fit its content using a ResizeObserver bridge.
 * Security: sandbox="allow-scripts" only — no same-origin access to parent.
 *
 * Minimized state: collapses to a compact bar showing the title with an
 * expand button. Never permanently dismissed — always recoverable.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Last-measured iframe height per ``genui_id``, persisted at module scope.
 *
 * MessageList virtualises rows (react-virtuoso), so a widget scrolled past
 * the overscan window is unmounted and later remounted. Without this cache
 * each remount restarted at the 200px default and animated up to the real
 * height as the iframe reloaded — a multi-frame size change that Virtuoso
 * chases by nudging ``scrollTop``, which is exactly the "scrollbar jitters
 * up and down as the widget enters/leaves the screen" report. Seeding the
 * initial height from here makes a remount land at its true size instantly,
 * so there's nothing for the scroller to chase.
 */
const HEIGHT_CACHE = new Map<string, number>();

/** Cap so a runaway widget can't grow unbounded inside the chat column. */
const MAX_HEIGHT = 800;

/**
 * How long after (re)mount we treat the cached height as a floor. While the
 * iframe reloads its document its body briefly measures near-zero before the
 * content paints; honouring those transient shrink reports would collapse
 * the row and re-introduce the jitter. Growth is always honoured; once the
 * window passes, genuine interactive shrinks (a user collapsing a section)
 * flow through normally.
 */
const SETTLE_MS = 700;

interface Props {
  html: string;
  title?: string;
  genui_id: string;
  onMessage: (text: string) => void;
  onAction: (payload: unknown) => void;
  onClose: () => void;
  /** Pin this widget into the Files panel as an artifact. When omitted the
   *  Save control is hidden. May be async; the button shows a saving state
   *  while it resolves. */
  onSave?: () => void | Promise<void>;
  /** True once the widget's file is registered as an artifact — swaps the
   *  Save control for a non-interactive "Saved" confirmation. */
  saved?: boolean;
}

/** Minimal JS bridge injected into every genui iframe. */
const BRIDGE_SCRIPT = `
<script>
(function() {
  var cowork = {
    sendMessage: function(text) {
      window.parent.postMessage({ type: 'cowork:message', text: String(text) }, '*');
    },
    sendAction: function(payload) {
      window.parent.postMessage({ type: 'cowork:action', payload: payload }, '*');
    },
    close: function() {
      window.parent.postMessage({ type: 'cowork:close' }, '*');
    }
  };
  window.cowork = cowork;

  // Auto-resize: report body height to parent
  function _reportHeight() {
    var h = Math.max(
      document.body.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.scrollHeight
    );
    window.parent.postMessage({ type: 'cowork:resize', height: h }, '*');
  }
  if (typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(_reportHeight).observe(document.body);
  }
  // Also report after load + images
  window.addEventListener('load', function() {
    _reportHeight();
    setTimeout(_reportHeight, 500);
  });
  document.addEventListener('DOMContentLoaded', _reportHeight);
})();
</script>
`;

export function GenUIFrame({ html, title, genui_id, onMessage, onAction, onClose, onSave, saved = false }: Props) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Seed from the per-widget cache so a virtualised remount opens at the
  // height it last settled on rather than the 200px default.
  const [height, setHeight] = useState(() => HEIGHT_CACHE.get(genui_id) ?? 200);
  const [minimized, setMinimized] = useState(false);
  const [saving, setSaving] = useState(false);
  // Wall-clock of this mount, used for the settle-window shrink guard.
  const mountedAt = useRef(Date.now());

  const displayTitle = title || "Interactive widget";

  const handleSave = useCallback(async () => {
    if (saving || saved) return;
    setSaving(true);
    try {
      await onSave?.();
    } finally {
      setSaving(false);
    }
  }, [saving, saved, onSave]);

  // Wrap the HTML with the bridge script
  const srcDoc = useMemo(() => {
    // If the HTML already has a </body> or </html>, inject before it
    // Otherwise append at the end
    const lowerHtml = html.toLowerCase();
    if (lowerHtml.includes("</body>")) {
      return html.replace(/<\/body>/i, `${BRIDGE_SCRIPT}</body>`);
    }
    if (lowerHtml.includes("</html>")) {
      return html.replace(/<\/html>/i, `${BRIDGE_SCRIPT}</html>`);
    }
    // Bare HTML fragment — wrap it
    return `<!DOCTYPE html>
<html><head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <style>
    * { box-sizing: border-box; }
    body { margin: 0; padding: 16px; font-family: system-ui, -apple-system, sans-serif; color: #1a1a2e; }
  </style>
</head><body>
${html}
${BRIDGE_SCRIPT}
</body></html>`;
  }, [html]);

  // Listen for postMessage from this iframe
  const handleMessage = useCallback((e: MessageEvent) => {
    if (e.source !== iframeRef.current?.contentWindow) return;
    const data = e.data;
    if (!data || typeof data !== "object") return;

    switch (data.type) {
      case "cowork:message":
        if (typeof data.text === "string" && data.text.trim()) {
          onMessage(data.text);
        }
        break;
      case "cowork:action":
        if (data.payload !== undefined) {
          onAction(data.payload);
        }
        break;
      case "cowork:close":
        setMinimized(true);
        onClose();
        break;
      case "cowork:resize":
        if (typeof data.height === "number" && data.height > 0) {
          const next = Math.min(data.height, MAX_HEIGHT);
          setHeight((prev) => {
            // Ignore transient shrink reports during the post-mount settle
            // window — those are the iframe's body measuring near-zero
            // mid-reload, and honouring them collapses the row and jitters
            // the scroll. Growth is always accepted.
            if (next < prev && Date.now() - mountedAt.current < SETTLE_MS) {
              return prev;
            }
            HEIGHT_CACHE.set(genui_id, next);
            return next;
          });
        }
        break;
    }
  }, [onMessage, onAction, onClose, genui_id]);

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [handleMessage]);

  // Minimized state — compact bar with expand button
  if (minimized) {
    return (
      <button
        onClick={() => setMinimized(false)}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-space-20/40
                   border border-space-30/50 hover:bg-space-20/80
                   transition-colors group
                   animate-[fadeInUp_150ms_ease-out]"
      >
        <span className="text-sm">🎨</span>
        <span className="text-xs font-medium text-space-50">{displayTitle}</span>
        <span className="text-[10px] text-space-40">(minimized)</span>
        <svg
          className="h-3.5 w-3.5 text-space-40 group-hover:text-space ml-auto transition-colors"
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
        </svg>
      </button>
    );
  }

  return (
    <div
      className="w-full animate-[fadeInUp_200ms_ease-out] rounded-xl border border-space-20
                 overflow-hidden bg-surface shadow-sm"
      data-genui-id={genui_id}
    >
      {/* Title bar — always shown (as minimize/expand control) */}
      <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-jove-10 to-surface border-b border-space-20">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm">🎨</span>
          <span className="text-sm text-jove font-semibold truncate">{displayTitle}</span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {/* Save-to-Files CTA: lets the user pin the widget into the Files
              panel without asking the agent to "make this an artifact". */}
          {onSave && (saved ? (
            <span
              className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-oxy"
              title="Saved to Files"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
              Saved
            </span>
          ) : (
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium text-jove
                         hover:bg-jove-30 transition-colors disabled:opacity-60 disabled:hover:bg-transparent"
              title="Save this widget to the Files panel"
            >
              {saving ? (
                <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={4} />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : (
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
              )}
              {saving ? "Saving…" : "Save to Files"}
            </button>
          ))}
          <button
            onClick={() => setMinimized(true)}
            className="p-1 rounded-md hover:bg-space-20 transition-colors"
            title="Minimize"
          >
            <svg className="w-3.5 h-3.5 text-space-40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M18 12H6" />
            </svg>
          </button>
        </div>
      </div>

      <iframe
        ref={iframeRef}
        srcDoc={srcDoc}
        sandbox="allow-scripts"
        className="w-full border-0"
        // No height transition: an animated height is a multi-frame size
        // change that Virtuoso chases by nudging scrollTop, which is what
        // produced the scroll jitter. Snapping to the measured height keeps
        // the row size stable in a single frame.
        style={{ height, minHeight: 60 }}
        title={displayTitle}
      />
    </div>
  );
}
